import { useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export interface ConfirmDialogState {
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  dialog,
  onClose,
}: {
  dialog: ConfirmDialogState;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center animate-fadeInBackdrop"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className="bg-[var(--surface-secondary)] rounded-2xl shadow-2xl p-6 w-[340px] animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              dialog.destructive ? 'bg-[var(--error)]/10' : 'bg-[var(--accent-subtle)]'
            }`}
          >
            <AlertTriangle
              className={`w-4 h-4 ${
                dialog.destructive ? 'text-[var(--error)]' : 'text-[var(--accent-primary)]'
              }`}
            />
          </div>
          <p className="text-[14px] font-semibold text-[var(--content-primary)]">
            {dialog.title}
          </p>
        </div>
        <p className="text-[13px] text-[var(--content-secondary)] mb-5">
          {dialog.message}
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[13px] text-[var(--content-primary)] bg-[var(--surface-tertiary)] rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              await dialog.onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-[13px] font-medium text-white rounded-xl hover:opacity-90 transition-colors ${
              dialog.destructive ? 'bg-[var(--error)]' : 'bg-[var(--accent-primary)]'
            }`}
          >
            {dialog.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
